// src/components/StripeSubscriptionButton.tsx
import React, { useState } from 'react';
import { Button, Box, CircularProgress, Paper, Typography, Divider } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ErrorModal from './ErrorModal';
import { useAuth } from '@/contexts/AuthProvider';
import { useDispatch } from 'react-redux';
import { setTemporaryPremium } from '@/store/premiumSlice';
import { trackSubscriptionEvent } from '@/utils/analytics';
import { recordFunnelBeforeNavigation } from '@/utils/funnel';
import { authedPost } from '@/services/apiClient';

interface StripeSubscriptionButtonProps {
    /** Stripe Price ID */
    priceId: string;
    /** すでに登録済みの SubscriptionID（なければ空文字） */
    existingSubscriptionID?: string;
    /** サンドボックスモードを使用するか */
    useSandbox?: boolean;
    /** ファネル記録の出どころ。価格ごとに売れた場所を分けて数えるために使う */
    source?: string;
    /** ボタンの文言（既定は「Stripeで登録する」。導線ガードがこの文言を見ている） */
    label?: string;
}

const StripeSubscriptionButton: React.FC<StripeSubscriptionButtonProps> = ({
    priceId,
    existingSubscriptionID = '',
    useSandbox = false,
    source = 'account_page',
    label,
}) => {
    const { user } = useAuth();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // 本番環境のエンドポイントを使用（サンドボックスモードはPriceIDで制御）
    const apiEndpoint = 'https://us-central1-chusho-app.cloudfunctions.net/api/stripe/create-checkout-session';

    /* -------------------------------------------------------------- */
    /* クリック処理 – Stripe Checkoutセッションを作成               */
    /* -------------------------------------------------------------- */
    const handleSubscribe = async () => {
        if (!user) return;

        if (existingSubscriptionID) {
            alert('すでにサブスクリプションが登録されています。');
            return;
        }

        // サブスクリプション開始をトラッキング（ファネル: checkout_start）
        trackSubscriptionEvent.startCheckout(priceId, 0); // 価格は後で設定可能
        // このあと Stripe へリダイレクトするので、書き込みの完了を待ってから進む
        // （撃ちっぱなしだと遷移で記録が消える。上限2.5秒で打ち切り、決済は止めない）
        await recordFunnelBeforeNavigation('checkout_start', user.uid, {
            source,
            price_id: priceId,
        });

        setIsLoading(true);
        setErrorMessage('');

        try {
            const requestData = {
                priceId,
                userId: user.uid,
                email: user.email || undefined,
                successUrl: `${window.location.origin}/?stripe_success=true&session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${window.location.origin}/?stripe_cancelled=true`,
                useSandbox, // サンドボックスモードかどうかをバックエンドに伝える
            };

            console.log('🚀 [Stripe Button] チェックアウトセッション作成リクエスト:', {
                endpoint: apiEndpoint,
                requestData,
            });

            const res = await authedPost('/stripe/create-checkout-session', requestData);

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error(await res.text());
            }
            const json = await res.json();

            console.log('📥 [Stripe Button] セッション作成レスポンス:', {
                status: res.status,
                ok: res.ok,
                response: json,
            });

            if (!res.ok) {
                throw new Error(json.error || 'Unknown error');
            }

            /* Redux に一時プレミアムをセットしてリダイレクト */
            dispatch(setTemporaryPremium(true));

            // 決済ページへは確認ダイアログを挟まずそのまま送る。
            // ここはファネル最終段の一番価値が高いクリックで、ブラウザ標準の alert は
            // 体裁が悪いうえに離脱要因になる（反映に数分かかる旨は決済後の画面で案内する）。

            // Stripeのチェックアウトページにリダイレクト
            if (json.url) {
                console.log('🔗 [Stripe Button] リダイレクト先:', json.url);
                window.location.href = json.url;
            } else {
                console.error('❌ [Stripe Button] URLが見つかりません');
            }
        } catch (e: any) {
            setErrorMessage(`支払い処理エラー: ${e.message}`);
            setIsLoading(false);
        }
    };

    const handleCloseError = () => setErrorMessage('');

    return (
        <>
            {/* テストモード時のカード情報表示 */}
            {useSandbox && (
                <Paper
                    sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: '#e3f2fd',
                        border: '2px dashed #1976d2',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <CreditCardIcon color="primary" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            テスト用カード情報
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>カード番号:</strong> 4242 4242 4242 4242
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>有効期限:</strong> 12/34 (将来の任意の日付)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>CVV:</strong> 123 (任意の3桁)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>郵便番号:</strong> 12345 (任意の番号)
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        ※ このカード情報では実際の課金は発生しません
                    </Typography>
                </Paper>
            )}

            {isLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 2,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSubscribe}
                    sx={{
                        px: 3,
                        py: 1,
                        fontSize: '1rem',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                        ':active': { transform: 'translateY(4px)', boxShadow: 'none' },
                    }}
                >
                    {label ?? (useSandbox ? '🧪 Stripeで登録する (テスト)' : 'Stripeで登録する')}
                </Button>
            )}

            {errorMessage && (
                <ErrorModal
                    open={true}
                    message={errorMessage}
                    onClose={handleCloseError}
                />
            )}
        </>
    );
};

export default StripeSubscriptionButton;
